import { useState } from "react";
import { Building2, PiggyBank, Plus, Upload, BarChart2, Users, Truck, DollarSign, Pencil, Home, Eye, EyeOff, UserPlus, Search, Star, Minus } from "lucide-react";
import TimeRangeSelector from "../components/TimeRangeSelector";
import AddTransactionDialog from "../components/AddTransactionDialog";
import AddProductDialog from "../components/AddProductDialog";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import CustomerListWithNotes, { CustomerWithMeta } from "../components/CustomerListWithNotes";
import SupplierListWithAdd, { SupplierEntry } from "../components/SupplierListWithAdd";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import ChartSwitcher from "../components/ChartSwitcher";
import MiniBarChart from "../components/MiniLineChart";
import UnifiedProfitChart from "../components/UnifiedProfitChart";
import ProfitSummaryBoxes from "../components/ProfitSummaryBoxes";

const DEMO = {
  logo: "https://placehold.co/64x64?text=Logo",
  name: "BBA", // Formerly 'Example Venture'/Consulting
};

const VENTURE_COLORS: Record<string,string> = {
  BBA: 'bg-teal-500',
  HSM: 'bg-orange-500',
  'Petal & Forever': 'bg-pink-500',
  Other: 'bg-slate-400',
};
const DEMO_CUSTOMERS: CustomerWithMeta[] = [
  { name: "Acme Corp.", value: "$5,000", rating: 4, notes: "Great repeat client. Pays net 7.", phone: "123-456-7890", email: "acme@email.com", ventures: ['BBA', 'HSM'] },
  { name: "Global Biz", value: "$2,800", rating: 3, notes: "Sometimes slow to order.", phone: "555-123-4567", email: "contact@global.biz", ventures: ['Petal & Forever'] },
];

const DEMO_SUPPLIERS: SupplierEntry[] = [
  { name: "Widget Supply", phone: "678-555-1122", email: "widget@foo.com" },
  { name: "BoxCo", phone: "789-999-3333", email: "info@boxco.net" },
];

const SECTIONS = [
  { key: "profit", label: "Profit vs Expenses" },
  { key: "customers", label: "Customers" },
  { key: "suppliers", label: "Suppliers" },
  { key: "financials", label: "Financials" },
  { key: "presets", label: "Products / Services" }, // <-- new tab
];

const TRANSACTIONS = [
  { id: 1, date: "2025-06-10 14:23", type: "Profit", item: "Super Apeti", amount: 40 },
  { id: 2, date: "2025-06-09 08:11", type: "Expense", item: "Fuel", amount: -30 },
  { id: 3, date: "2025-06-07 18:22", type: "Profit", item: "Maca Plus", amount: 40 },
  { id: 4, date: "2025-06-05 20:31", type: "Expense", item: "Apeti Supplier", amount: -40 },
  { id: 5, date: "2025-06-03 13:05", type: "Profit", item: "Flat Tummy Capsules", amount: 40 },
];

const profitLine = [
  { date: "6/10", amount: 2000 },
  { date: "6/11", amount: 2250 },
  { date: "6/12", amount: 2400 },
  { date: "6/13", amount: 2100 },
  { date: "6/14", amount: 2600 },
  { date: "6/15", amount: 2800 },
  { date: "6/16", amount: 3255 },
];
const expenseLine = [
  { date: "6/10", amount: 83 },
  { date: "6/11", amount: 75 },
  { date: "6/12", amount: 71 },
  { date: "6/13", amount: 60 },
  { date: "6/14", amount: 108 },
  { date: "6/15", amount: 108 },
  { date: "6/16", amount: 117 },
];

const DEFAULT_PRODUCTS = [
  { id: 1, name: "Super Apeti", amount: 40 },
  { id: 2, name: "Maca Plus", amount: 40 },
  { id: 3, name: "Flat Tummy Capsules", amount: 40 },
  { id: 4, name: "Waist trainers", amount: 40 },
];

// ... store purchases per customer: add purchases: {date:string, amount:number, item:string}[] to customers
// Whenever a transaction is added (onAdd in AddTransactionDialog), if profit and linked to a customer, push an entry into that customer's .purchases[]
// In customerModal: analyze purchase dates
function getPurchasePattern(purchases) {
  if (!purchases || purchases.length < 3) return null;
  // Get sorted date ints
  const dates = purchases.map(p => new Date(p.date).getTime()).sort((a,b)=>a-b);
  const diffs = dates.slice(1).map((t,i) => t-dates[i]);
  const avgDiff = diffs.reduce((a,b)=>a+b,0) / diffs.length;
  const days = Math.round(avgDiff/(24*3600*1000));
  // Find if falls on same day of week
  const daysOfWeek = purchases.map(p => new Date(p.date).getDay());
  const allSameDay = daysOfWeek.every(d => d===daysOfWeek[0]);
  if (allSameDay && days <= 8) {
    return `Typically purchases every ${["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"][daysOfWeek[0]]}`;
  }
  if (days>=13&&days<=16) return "Buys roughly every 2 weeks";
  if (days>=6&&days<=8)  return "Buys weekly";
  if (days>=3&&days<=5)  return "Buys 2x/week";
  if (days>=27&&days<=33) return "Buys monthly";
  return null;
}

const VentureDetail = ({ name = DEMO.name }: { name?: string }) => {
  const [logo, setLogo] = useState(DEMO.logo);
  const [section, setSection] = useState("profit");
  const [range, setRange] = useState("mtd");
  const [addOpen, setAddOpen] = useState(false);
  const [addProductOpen, setAddProductOpen] = useState(false);
  const [transactions, setTransactions] = useState(TRANSACTIONS);
  const [productType, setProductType] = useState<'products'|'services'>('products');
  const [customers, setCustomers] = useState<CustomerWithMeta[]>(DEMO_CUSTOMERS);
  const [suppliers, setSuppliers] = useState<SupplierEntry[]>(DEMO_SUPPLIERS);
  const [abbrevDates, setAbbrevDates] = useState(false);
  const [products, setProducts] = useState(DEFAULT_PRODUCTS);
  const [customerModal, setCustomerModal] = useState<null|CustomerWithMeta>(null);
  const [customerFilter, setCustomerFilter] = useState<'az'|'za'|'recent'>('az');
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerStarFilter, setCustomerStarFilter] = useState(0);
  const [supplierSearch, setSupplierSearch] = useState("");
  const [supplierStarFilter, setSupplierStarFilter] = useState(0);
  const [supplierFilter, setSupplierFilter] = useState<'az'|'za'|'recent'>('az');
  const navigate = useNavigate();

  // Add per-venture editable profit goal with a default
  // ... add state to track per-period goals ...
  const [profitGoal, setProfitGoal] = useState({
    WTD: 1000,
    MTD: 3000,
    YTD: 15000
  });
  const [editingGoal, setEditingGoal] = useState<"WTD"|"MTD"|"YTD"|false>(false);

  // New state for goal range selector
  // Set default for goalRange:
  const [goalRange, setGoalRange] = useState<"YTD"|"MTD"|"WTD">("YTD");
  const today = new Date();
  function getMonday(d:Date) { const date = new Date(d); const day = date.getDay(), diff = date.getDate() - day + (day === 0 ? -6 : 1); return new Date(date.setDate(diff)); }
  const weekNum = ((date:Date)=> {
    const firstJan = new Date(date.getFullYear(),0,1);
    return Math.ceil( (((date.getTime() - firstJan.getTime())/86400000)+firstJan.getDay()+1)/7 );
  })(today);
  const mondayThisMonth = getMonday(new Date(today.getFullYear(), today.getMonth(), 1));
  const weeksThisMonth = Math.ceil((today.getDate() + (mondayThisMonth.getDay() ? mondayThisMonth.getDay()-1 : 6)) / 7);
  // ...update all references to profitGoal to use profitGoal[goalRange] ...
  const scaledGoal = goalRange==="WTD" ? profitGoal.WTD : goalRange==="MTD" ? profitGoal.MTD : profitGoal.YTD;

  // New state for toggling full/abbreviated transaction date display
  const [showFullTxDate, setShowFullTxDate] = useState(false);
  function formatDate(dt: string) {
    if (!abbrevDates) return dt;
    // Try MM/DD or fallback
    let d = new Date(dt);
    if (!isNaN(d.getTime())) return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    // Fallback: shorten
    return dt.substring(0,10);
  }

  // New format function for transaction dates with toggle
  function formatTxDate(s:string, full=false) {
    const d = new Date(s);
    if(isNaN(d.getTime())) return s;
    if(full) return d.toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'});
    return `${d.getMonth()+1}/${d.getDate()}`;
  }

  // Helper: get data for selected date range (WTD/MTD/YTD)
  function getPeriodData(arr, range) {
    const now = new Date();
    let start: Date;
    if (range === "WTD") { // Start Monday
      start = new Date(now); start.setDate(now.getDate() - ((now.getDay()+6)%7));
    } else if (range === "MTD") {
      start = new Date(now.getFullYear(), now.getMonth(), 1);
    } else { // YTD
      start = new Date(now.getFullYear(), 0, 1);
    }
    return arr.filter(t => {
      const d = new Date(t.date);
      return d >= start && d <= now;
    });
  }

  // For line chart demo data per day:
  function getDaysInPeriod(range) {
    const now = new Date();
    let start;
    if (range === "WTD") { start = new Date(now); start.setDate(now.getDate() - ((now.getDay()+6)%7)); }
    else if (range === "MTD") { start = new Date(now.getFullYear(), now.getMonth(), 1); }
    else { start = new Date(now.getFullYear(), 0, 1); }
    const days = [];
    for(let d = new Date(start); d <= now; d.setDate(d.getDate()+1)) days.push(new Date(d));
    return days;
  }

  const periodTransactions = getPeriodData(transactions||[], goalRange);
  const periodProfit = (periodTransactions.filter(t=>t.type.toLowerCase()==='profit').reduce((a,b)=>a+(+b.amount||0),0))||0;
  const periodExpense = (periodTransactions.filter(t=>t.type.toLowerCase()==='expense').reduce((a,b)=>a+(+b.amount||0),0))||0;

  // After getDaysInPeriod(goalRange):
  const days = getDaysInPeriod(goalRange);

  // Ensure correct mapping for profitPoints and expensePoints
  const profitPoints = days.map(date=> {
    const dStr = date.toISOString().slice(0,10);
    return {
      date: dStr,
      amount: (transactions||[]).filter(t=>t.type.toLowerCase()==='profit' && t.date.slice(0,10)===dStr).reduce((a,b)=>a+(+b.amount||0),0)
    };
  });
  const expensePoints = days.map(date=> {
    const dStr = date.toISOString().slice(0,10);
    return {
      date: dStr,
      amount: (transactions||[]).filter(t=>t.type.toLowerCase()==='expense' && t.date.slice(0,10)===dStr).reduce((a,b)=>a+(+Math.abs(b.amount)||0),0)
    };
  });

  // Calculate month-to-date and trend/projection
  const totalProfit = transactions?.filter(t=>t.type.toLowerCase()==='profit').reduce((sum,t)=>sum+(+t.amount||0),0) || 0;
  const daysInCurrentMonth = (new Date()).getDate();
  const totalTransactions = transactions?.filter(t=>t.type.toLowerCase()==='profit').length || 1;
  const avgDaily = totalProfit / (daysInCurrentMonth || 1);
  const daysInMonth = new Date(new Date().getFullYear(), new Date().getMonth()+1, 0).getDate();
  const projectedProfit = Math.round(avgDaily * daysInMonth);
  const profitNeeded = Math.max(0, scaledGoal-totalProfit);
  const avgSale = totalProfit/totalTransactions;
  const salesNeeded = avgSale > 0 ? Math.ceil(profitNeeded/avgSale) : profitNeeded;
  const isOnPace = projectedProfit >= scaledGoal;

  // Prepare projected points for right chart
  const avgPerDay = avgDaily;

  // Additional variables for new suggestion box
  // Calculate week start date for WTD calculations
  const weekStartDate = new Date(today);
  weekStartDate.setDate(today.getDate() - ((today.getDay() + 6) % 7));

  // Filter sales (profit transactions) in current goal range for avg sale calculation and message
  const sales = periodTransactions.filter(t => t.type.toLowerCase() === 'profit');

  // Calculate totals for ProfitSummaryBoxes props
  const totalProfitForSummary = profitPoints.reduce((sum, p) => sum + p.amount, 0);
  const totalExpensesForSummary = expensePoints.reduce((sum, p) => sum + p.amount, 0);

  // Add simplified mode state after other states:
  const [simplifiedMode, setSimplifiedMode] = useState(false);

  // Calculate today's totals for simplified mode:
  const todayISO = new Date().toISOString().slice(0,10);
  const todayTransactions = transactions.filter(t => t.date.slice(0,10) === todayISO);
  const todayProfit = todayTransactions.filter(t => t.type.toLowerCase() === 'profit').reduce((sum, t) => sum + (t.amount || 0), 0);
  const todayExpenses = todayTransactions.filter(t => t.type.toLowerCase() === 'expense').reduce((sum, t) => sum + Math.abs(t.amount || 0), 0);
  const todayNet = todayProfit - todayExpenses;

  // Function to quickly add a product/service:
  const quickAddProduct = (product: any, type: 'profit' | 'expense') => {
    const amount = type === 'profit' ? product.amount : -Math.abs(product.amount);
    setTransactions(prev => [{
      id: Date.now(),
      date: new Date().toLocaleString(),
      type: type === 'profit' ? 'Profit' : 'Expense',
      item: product.name,
      amount: amount,
    }, ...prev]);
    toast.success(`Added ${type === 'profit' ? 'Profit' : 'Expense'}: ${product.name}`);
  };
  return (
    <div className="min-h-screen flex flex-col items-center bg-gradient-to-b from-slate-50 to-white dark:from-background dark:to-card">
      {/* Home icon top left */}
      <button
        onClick={() => navigate("/")}
        aria-label="Go to Dashboard"
        className="absolute left-4 top-4 bg-white dark:bg-card rounded-full p-2 shadow hover:bg-primary/10 text-primary hover:text-primary-foreground transition z-20 border border-border"
        style={{lineHeight:0}}
      >
        <Home size={22} />
      </button>
      <div className="w-full max-w-4xl mt-20 mb-8 bg-card rounded-2xl shadow-xl px-0 sm:px-8 py-8 flex flex-col items-center">
        <div className="flex flex-col sm:flex-row justify-between gap-4 w-full items-start">
          <div className="flex gap-4 items-center flex-1 min-w-0">
            <label className="relative inline-block cursor-pointer group">
              <img src={logo} alt="Logo" className="w-14 h-14 rounded-full ring-2 ring-primary object-cover bg-muted shadow-md" />
              <input type="file" className="hidden" accept="image/*" onChange={e => {
                const f = e.target.files?.[0];
                if (f) setLogo(URL.createObjectURL(f));
              }} />
              <span className="absolute bottom-0 right-0 bg-primary text-white rounded-full p-1 border border-card group-hover:scale-110 transition-transform"><Upload size={16} /></span>
            </label>
            <span className="text-2xl sm:text-3xl font-bold text-primary min-w-0 truncate">{name}</span>
          </div>
          {/* Section nav: horizontally scrollable tab-bar */}
          <nav className="flex gap-2 mt-2 sm:mt-0 w-full overflow-x-auto scrollbar-thin scrollbar-thumb-muted-foreground/20 scrollbar-track-transparent px-1 whitespace-nowrap pb-2 hide-scrollbar" style={{WebkitOverflowScrolling:'touch'}}>
            {SECTIONS.map(s => (
              <button
                key={s.key}
                className={`px-3 py-1.5 rounded font-medium transition text-base min-w-[142px] sm:min-w-[108px] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${section === s.key ? "bg-primary text-primary-foreground shadow-sm" : "bg-muted hover:bg-primary/10 text-muted-foreground"}`}
                onClick={() => setSection(s.key)}
              >
                {s.label}
              </button>
            ))}
          </nav>
        </div>
        {/* Section content */}
        <div className="w-full mt-8">
          {section === "profit" && (
            <>
              {/* Simplified Mode Toggle */}
              <div className="mb-4 flex justify-end">
                <button
                  onClick={() => setSimplifiedMode(!simplifiedMode)}
                  className={`px-4 py-2 rounded-lg font-medium transition ${
                    simplifiedMode 
                      ? "bg-primary text-primary-foreground" 
                      : "bg-muted hover:bg-primary/10 text-muted-foreground"
                  }`}
                >
                  {simplifiedMode ? "Detailed View" : "Simplify"}
                </button>
              </div>

              {simplifiedMode ? (
                /* Simplified Mode Layout */
                <div className="space-y-6">
                  {/* Today's Progress */}
                  <div className="bg-white rounded-lg shadow-md p-6">
                    <h2 className="text-xl font-semibold mb-4 text-center">Today's Progress</h2>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center bg-green-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600 font-medium">Profit</p>
                        <p className="text-2xl font-bold text-green-600">${todayProfit}</p>
                      </div>
                      <div className="text-center bg-red-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600 font-medium">Expenses</p>
                        <p className="text-2xl font-bold text-red-600">${todayExpenses}</p>
                      </div>
                      <div className={`text-center rounded-lg p-4 ${todayNet >= 0 ? 'bg-blue-50' : 'bg-orange-50'}`}>
                        <p className="text-sm text-gray-600 font-medium">Net</p>
                        <p className={`text-2xl font-bold ${todayNet >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>${todayNet}</p>
                      </div>
                    </div>
                  </div>

                  {/* Quick Add Sections */}
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Add Profit Section */}
                    <div className="bg-green-50 rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-green-800 mb-4 flex items-center gap-2">
                        <Plus className="text-green-600" size={20} />
                        Add Profit
                      </h3>
                      <div className="grid gap-2">
                        {products.map(product => (
                          <button
                            key={product.id}
                            onClick={() => quickAddProduct(product, 'profit')}
                            className="bg-white hover:bg-green-100 border border-green-200 rounded-lg p-3 text-left transition flex justify-between items-center"
                          >
                            <span className="font-medium">{product.name}</span>
                            <span className="text-green-600 font-bold">${product.amount}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Add Expense Section */}
                    <div className="bg-red-50 rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-red-800 mb-4 flex items-center gap-2">
                        <Minus className="text-red-600" size={20} />
                        Add Expense
                      </h3>
                      <div className="grid gap-2">
                        {[
                          { id: 1, name: "Ad Spend", amount: 80 },
                          { id: 2, name: "Shipping", amount: 15 },
                          { id: 3, name: "Returns/Refund", amount: 50 }
                        ].map(expense => (
                          <button
                            key={expense.id}
                            onClick={() => quickAddProduct(expense, 'expense')}
                            className="bg-white hover:bg-red-100 border border-red-200 rounded-lg p-3 text-left transition flex justify-between items-center"
                          >
                            <span className="font-medium">{expense.name}</span>
                            <span className="text-red-600 font-bold">${expense.amount}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Today's Transactions */}
                  <div className="bg-white rounded-lg shadow-md p-6">
                    <h3 className="text-lg font-semibold mb-4">Today's Transactions</h3>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {todayTransactions.length === 0 ? (
                        <p className="text-muted-foreground italic">No transactions today yet.</p>
                      ) : (
                        todayTransactions.map(tx => (
                          <div key={tx.id} className="flex justify-between items-center p-2 bg-muted/30 rounded">
                            <span className="font-medium">{tx.item}</span>
                            <span className={`font-bold ${tx.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {tx.amount > 0 ? '+' : ''}${Math.abs(tx.amount)}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                /* Original Detailed View */
                <>
                  <AddTransactionDialog
                    open={addOpen}
                    onOpenChange={setAddOpen}
                    onAdd={tx => {
                      setTransactions(prev => [{
                        id: Date.now(),
                        date: new Date().toLocaleString(),
                        type: tx.type,
                        item: tx.item,
                        amount: tx.amount,
                        party: tx.party,
                      }, ...prev]);
                      // If new profit-linked customer
                      if (tx.type === 'Profit' && tx.party && tx.party.name) {
                        if (!customers.some(c => c.name.toLowerCase() === tx.party.name.toLowerCase())) {
                          setCustomers(cur => [
                            ...cur,
                            { name: tx.party.name, phone: tx.party.phone, email: tx.party.email, notes: '', rating: 0, ventures: [], purchases: [{date: new Date().toISOString(), amount: tx.amount, item: tx.item}] }
                          ]);
                        } else {
                          // Add purchase to existing customer
                          setCustomers(cur => cur.map(c => {
                            if (c.name.toLowerCase() === tx.party.name.toLowerCase()) {
                              const newPurchases = c.purchases ? [...c.purchases, {date: new Date().toISOString(), amount: tx.amount, item: tx.item}] : [{date: new Date().toISOString(), amount: tx.amount, item: tx.item}];
                              return {...c, purchases: newPurchases};
                            }
                            return c;
                          }));
                        }
                      }
                      // If new expense-linked supplier
                      if (tx.type === 'Expense' && tx.party && tx.party.name) {
                        if (!suppliers.some(s => s.name.toLowerCase() === tx.party.name.toLowerCase())) {
                          setSuppliers(cur => {
                            const next = [...cur, { name: tx.party.name, phone: tx.party.phone, email: tx.party.email }];
                            console.log('[SupplierAdd]', next);
                            return next;
                          });
                        }
                      }
                      toast.success(`Added ${tx.type === 'Profit' ? 'Income' : 'Expense'}: ${tx.item}`);
                    }}
                    ventureName={name}
                    customerList={customers}
                    supplierList={suppliers}
                    productsList={products}
                  />
                  <main className="container mx-auto px-4 sm:px-6 md:px-8 py-6">
                    <section className="mb-8 bg-white rounded-lg shadow-md p-6">
                      <div className="flex justify-end mb-2">
                        <div className="flex gap-2">
                          <button
                            className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg flex items-center gap-2 transition text-base shadow"
                            onClick={() => { setAddOpen(true); /* Set default Profit in modal if available */ }}
                            aria-label="Add Profit"
                          >
                            <Plus size={18}/> Add Profit
                          </button>
                          <button
                            className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg flex items-center gap-2 transition text-base shadow"
                            onClick={() => { setAddOpen(true); /* Set default Expense in modal if available */ }}
                            aria-label="Add Expense"
                          >
                            <Minus size={18}/> Add Expense
                          </button>
                        </div>
                      </div>
                      {/* Change button order inside the section: */}
                      <div className="mb-4 flex items-center justify-between">
                        <div className="flex gap-2">
                          <button
                            className={`px-3 py-1.5 rounded font-medium transition text-base focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${goalRange === "YTD" ? "bg-primary text-primary-foreground shadow-sm" : "bg-muted hover:bg-primary/10 text-muted-foreground"}`}
                            onClick={() => setGoalRange("YTD")}
                          >
                            YTD
                          </button>
                          <button
                            className={`px-3 py-1.5 rounded font-medium transition text-base focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${goalRange === "MTD" ? "bg-primary text-primary-foreground shadow-sm" : "bg-muted hover:bg-primary/10 text-muted-foreground"}`}
                            onClick={() => setGoalRange("MTD")}
                          >
                            MTD
                          </button>
                          <button
                            className={`px-3 py-1.5 rounded font-medium transition text-base focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${goalRange === "WTD" ? "bg-primary text-primary-foreground shadow-sm" : "bg-muted hover:bg-primary/10 text-muted-foreground"}`}
                            onClick={() => setGoalRange("WTD")}
                          >
                            WTD
                          </button>
                        </div>
                      </div>

                      <ProfitSummaryBoxes className="mt-2" totalProfit={totalProfitForSummary} totalExpenses={totalExpensesForSummary} />

                      <div className="mt-6">
                        <h2 className="text-lg font-semibold">Profit Goal ({goalRange})</h2>
                        <div className="mb-6 flex flex-col sm:flex-row gap-4 sm:items-end justify-center">
                          <div className="bg-muted rounded-lg p-6 flex flex-col gap-2 shadow-inner border border-muted-foreground/10 max-w-xs mx-auto w-full sm:w-auto">
                            <div className="flex items-center gap-3 justify-center mb-1">
                              <div className="text-sm font-semibold text-primary flex items-center gap-2">Set a WTD goal (typical week's profit goal); MTD/YTD scale automatically.</div>
                            </div>
                            <span className="inline-block px-4 py-2 rounded-lg text-3xl font-mono font-bold bg-card text-primary/95 tracking-wide border border-primary/10 transition flex gap-2 items-center justify-center mx-auto">
                              ${profitGoal[goalRange].toLocaleString()} <span className="text-sm text-muted-foreground">/ {goalRange}</span>
                              <button className="ml-2 text-primary/70 hover:text-primary" onClick={()=>setEditingGoal(goalRange)} aria-label="Edit Goal"><Pencil size={20}/></button>
                            </span>
                            {editingGoal===goalRange && (
                              <input
                                type="number"
                                min={0}
                                className="border focus:border-primary/80 outline-none rounded px-3 py-2 w-32 text-3xl font-mono font-bold transition bg-background visible mt-3 mx-auto block"
                                value={profitGoal[goalRange]||""}
                                onChange={e=>setProfitGoal({...profitGoal,[goalRange]:parseInt(e.target.value)||0})}
                                onBlur={()=>setEditingGoal(false)}
                                autoFocus
                                aria-label="Edit profit goal"
                              />
                            )}
                            {!editingGoal && profitGoal.WTD===1000 && profitGoal.MTD===3000 && profitGoal.YTD===15000 && (
                              <span className="text-muted-foreground text-xs mt-2 text-center block">(Default, change anytime)</span>
                            )}
                          </div>
                          {/* Real-time suggestion box for sales/services needed or ahead */}
                          <div className="flex-1 max-w-md mx-auto sm:mx-0 mt-4 sm:mt-0 px-4 py-3 bg-muted/60 rounded-lg text-[15px] text-muted-foreground shadow-sm font-medium text-center sm:text-left">
                            {
                              (() => {
                                const curProfit = profitPoints.reduce((sum,p)=>(goalRange==='YTD'?sum+((new Date(p.date).getFullYear()===new Date().getFullYear())?p.amount:0):(goalRange==='MTD'?sum+((new Date(p.date).getMonth()===new Date().getMonth())?p.amount:0):(goalRange==='WTD'?sum+((new Date(p.date).getTime()>=weekStartDate.getTime())?p.amount:0):sum))),0);
                                const goal = profitGoal[goalRange];
                                const avgSale = sales.length ? sales.reduce((acc,tx)=>acc+tx.amount,0)/sales.length : 0;
                                const toGoal = Math.max(0, goal-curProfit);
                                let msg="";
                                if(curProfit<goal) {
                                  if(avgSale>0) {
                                    const needed = Math.ceil(toGoal/avgSale);
                                    msg=`You need ~${needed} more sale${needed!==1?"s":""} to hit your ${goalRange} goal.`;
                                  } else {
                                    msg=`You need $${toGoal.toLocaleString()} more profit to hit your ${goalRange} goal.`;
                                  }
                                } else {
                                  const ahead = Math.round((curProfit-goal)/avgSale)||0;
                                  msg=ahead>0
                                    ? `You're about ${ahead} sale${ahead!==1?"s":""} ahead of your ${goalRange} goal—awesome!`
                                    : `You've passed your ${goalRange} goal—congrats!`;
                                }
                                return msg;
                              })()
                            }
                          </div>
                        </div>
                      </div>
                    </section>

                    <section className="mb-6 bg-white rounded-lg shadow-md p-6">
                      <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-semibold text-foreground">Transaction History</h2>
                        <button className="bg-primary text-primary-foreground rounded-lg px-4 py-2 font-medium flex items-center gap-2 hover:bg-primary/90 transition" onClick={() => setAddOpen(true)}><Plus size={18} /> Add</button>
                      </div>
                      <div className="overflow-x-auto w-full">
                        <table className="w-full text-sm bg-background rounded-lg border border-muted-foreground/20">
                          <thead>
                            <tr className="text-muted-foreground border-b border-muted-foreground/20">
                              <th className="px-3 py-2 text-left font-semibold flex items-center gap-2">
                                Date/Time
                                <button
                                  type="button"
                                  onClick={()=>setShowFullTxDate(v=>!v)}
                                  aria-label={showFullTxDate ? "Show abbreviated date" : "Show full date"}
                                  className="ml-1 p-1 rounded hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary group"
                                  title={showFullTxDate ? "Show abbreviated date" : "Show full date"}
                                >
                                  {showFullTxDate ? <Eye size={17} className="opacity-70 group-hover:opacity-100 transition"/> : <EyeOff size={17} className="opacity-70 group-hover:opacity-100 transition"/>}
                                </button>
                              </th>
                              <th className="px-3 py-2 text-left font-semibold">Type</th>
                              <th className="px-3 py-2 text-left font-semibold">Product/Expense</th>
                              <th className="px-3 py-2 text-right font-semibold">Amount</th>
                              <th className="px-3 py-2 font-semibold text-right">Edit</th>
                            </tr>
                          </thead>
                          <tbody>
                            {transactions.map(tx => (
                              <tr key={tx.id} className="border-b last:border-0 hover:bg-muted/30 transition cursor-default">
                                <td className="px-3 py-2 whitespace-nowrap">{formatTxDate(tx.date, showFullTxDate)}</td>
                                <td className={"px-3 py-2 whitespace-nowrap font-medium " + (tx.type === "Profit" ? "text-green-700" : "text-destructive")}>{tx.type}</td>
                                <td className="px-3 py-2 whitespace-nowrap">{tx.item}</td>
                                <td className={"px-3 py-2 whitespace-nowrap font-semibold text-right " + (tx.amount > 0 ? "text-primary" : "text-destructive")}>{tx.amount > 0 ? `+$${tx.amount}` : `-$${Math.abs(tx.amount)}`}</td>
                                <td className="px-3 py-2 text-right">
                                  <button className="hover:bg-muted rounded p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary transition" aria-label="Edit transaction"><Pencil size={16} /></button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </section>

                    <section className="mb-6 bg-white rounded-lg shadow-md p-6">
                      <h2 className="text-xl font-semibold mb-4">Profit vs Expenses & Goal Pace</h2>
                      {console.log('profitPoints', profitPoints)}
                      {console.log('expensePoints', expensePoints)}
                      <UnifiedProfitChart
                        profitData={profitPoints}
                        expenseData={expensePoints}
                        goal={profitGoal[goalRange]}
                        period={goalRange}
                      />
                      <div className="flex gap-6 text-xs mt-4">
                        <span className="text-muted-foreground">Projected: <b className="text-primary">${projectedProfit.toLocaleString()}</b> <span className="ml-1 italic font-normal">(ghost trend)</span></span>
                        <span className={isOnPace?"text-green-700":"text-red-600"}>{isOnPace ? "On track to hit goal!" : `Need ${salesNeeded} more sale${salesNeeded===1?'':'s'} ($${profitNeeded}) to stay on pace.`}</span>
                      </div>
                    </section>
                  </main>
                </>
              )}
            </>
          )}
          {section === "customers" && (
            <div className="bg-muted rounded-xl p-6 shadow-inner">
              <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between w-full">
                <div className="flex gap-2 items-center">
                  <button className="bg-primary text-primary-foreground rounded-lg px-3 py-2 font-medium flex items-center gap-2 hover:bg-primary/90 transition" onClick={() => {
                    const canPickContacts = ('contacts' in navigator && 'ContactsManager' in window);
                    if (canPickContacts) {
                      // @ts-ignore
                      navigator.contacts.select(['name', 'email', 'tel'], {multiple:false}).then((contacts:any[])=>{
                        if (contacts && contacts[0]) {
                          const c = contacts[0];
                          setCustomers(cur=>[
                            ...cur,
                            {
                              name:(c.name&&c.name[0])||'',
                              email:(c.email&&c.email[0])||'',
                              phone:(c.tel&&c.tel[0])||'',
                              notes:'', value:'', rating:0, ventures: []
                            }
                          ]);
                        }
                      });
                    } else {
                      alert('Contact Picker not supported in this browser.');
                    }
                  }}>
                    <UserPlus size={16}/>
                    Pick from Contacts
                  </button>
                  <button className="bg-background border px-3 py-2 rounded font-medium text-sm flex gap-1 items-center" onClick={()=>{
                    const name = prompt('Customer name?');
                    if (!name) return;
                    setCustomers(c=>[...c,{name,notes:'',value:'',rating:0, ventures: []}]);
                  }}>
                    + Add Manually
                  </button>
                </div>
                <select
                  className="ml-auto border px-2 py-1 rounded text-sm text-muted-foreground"
                  value={customerFilter}
                  onChange={e=>setCustomerFilter(e.target.value as any)}
                  aria-label="Sort/filter"
                >
                  <option value="az">A–Z</option>
                  <option value="za">Z–A</option>
                  <option value="recent">Most Recent</option>
                </select>
              </div>
              <div className="mb-3 flex flex-col sm:flex-row sm:items-center gap-3 w-full justify-between">
                <div className="flex items-center gap-2 w-full max-w-sm">
                  <span className="text-muted-foreground"><Search size={18}/></span>
                  <input
                    type="text"
                    className="border bg-background rounded px-3 py-2 w-full text-sm"
                    placeholder="Search customers..."
                    value={customerSearch}
                    onChange={e=>setCustomerSearch(e.target.value)}
                    aria-label="Search customers"
                  />
                  <select
                    className="ml-1 border px-2 py-1 rounded text-sm text-muted-foreground"
                    value={customerStarFilter}
                    onChange={e=>setCustomerStarFilter(Number(e.target.value))}
                    aria-label="Filter by rating"
                  >
                    <option value={0}>All ★</option>
                    <option value={5}>★5</option>
                    <option value={4}>★4 & up</option>
                    <option value={3}>★3 & up</option>
                    <option value={2}>★2 & up</option>
                    <option value={1}>★1 & up</option>
                  </select>
                </div>
              </div>
              {/* Filter, sort, render customer list */}
              <div className="max-h-[325px] overflow-y-auto rounded-xl border bg-background divide-y divide-muted-foreground/6 mt-2">
                {[...customers]
                    .filter(c => !customerSearch ||
                      (c.name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
                       c.email?.toLowerCase().includes(customerSearch.toLowerCase()) ||
                       c.phone?.toLowerCase().includes(customerSearch.toLowerCase()))
                    )
                    .filter(c => customerStarFilter === 0 || (c.rating||0) >= customerStarFilter)
                    .sort((a,b)=>{
                      if (customerFilter==='recent') return 0; // preserve order
                      if (customerFilter==='az') return (a.name||'').localeCompare(b.name||'');
                      if (customerFilter==='za') return (b.name||'').localeCompare(a.name||'');
                      return 0;
                    })
                    .map((c,i) => (
                      <div key={c.name+i}
                           className="px-4 py-3 flex items-center gap-3 hover:bg-muted/70 transition cursor-pointer group"
                           onClick={()=>setCustomerModal(c)}>
                        <div className="flex-1 min-w-0">
                          <div className="flex gap-1 items-center mb-1">
                            <div className="font-semibold text-primary truncate">{c.name}</div>
                            {/* Venture chips */}
                            {(c.ventures||[]).map(vName=>(
                              <span
                                key={vName}
                                className={`ml-1 px-2 py-0.5 rounded-full text-xs font-bold text-white ${VENTURE_COLORS[vName]||VENTURE_COLORS['Other']}`}
                                title={vName}
                              >{vName}</span>
                            ))}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">{c.email||c.phone||c.value||''}</div>
                        </div>
                        {typeof c.rating==='number'&&c.rating>0 && <span className="text-xs text-yellow-600 font-bold ml-2">★{c.rating}</span>}
                      </div>
                    ))}
              </div>
              {customerModal && (
                <Dialog open={true} onOpenChange={()=>setCustomerModal(null)}>
                  <DialogContent>
                    <DialogTitle>Customer Details: {customerModal.name}</DialogTitle>
                    <DialogDescription>Manage this customer's info, notes, and business associations.</DialogDescription>
                    <div className="mb-4">
                      <div className="text-sm text-muted-foreground mb-1">{customerModal.email||'-'} | {customerModal.phone||'-'}</div>
                      <div className="mt-2 mb-2 text-sm text-muted-foreground">{customerModal.notes||<span className='italic'>No notes yet.</span>}</div>
                      {customerModal.purchases && customerModal.purchases.length>0 && (
                        <div className="bg-muted px-3 py-2 rounded mb-2 text-xs">
                          <div className="font-bold text-primary mb-1">Purchase History:</div>
                          {getPurchasePattern(customerModal.purchases) ? (
                            <div className="text-green-700 mb-1">{getPurchasePattern(customerModal.purchases)}</div>
                          ) : null}
                          <div>Recent: {customerModal.purchases.slice(-4).map(p=>(<span className="inline-block mr-2 mb-1" key={p.date}>{new Date(p.date).toLocaleDateString()}</span>))}</div>
                        </div>
                      )}
                    </div>
                    <form onSubmit={e=>{
                      e.preventDefault();
                      let v=(e.currentTarget as any).rating?.value||3;
                      let notes=(e.currentTarget as any).notes?.value||'';
                      // Get selected ventures from checkboxes
                      const formData = new FormData(e.currentTarget);
                      const selectedVentures = formData.getAll('ventures') as string[];
                      setCustomers(cus=>cus.map(c=>c.name===customerModal.name?{...c,rating:+v,notes, ventures: selectedVentures}:{...c}));
                      setCustomerModal(null);
                    }}>
                      <label className="text-xs text-muted-foreground">Review/rating:</label>
                      <select name='rating' defaultValue={customerModal.rating||"3"} className="block w-full my-1 border rounded">
                        <option value="5">5 - Excellent</option><option value="4">4</option><option value="3">3 - Good</option><option value="2">2</option><option value="1">1 - Poor</option>
                      </select>
                      <label className="text-xs text-muted-foreground mt-2 block">Linked Ventures:</label>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {Object.keys(VENTURE_COLORS).filter(v => v !== 'Other').map(vName => (
                          <label key={vName} className="inline-flex items-center gap-1 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              name="ventures"
                              value={vName}
                              defaultChecked={(customerModal.ventures||[]).includes(vName)}
                              className="cursor-pointer"
                            />
                            <span className={`px-2 py-0.5 rounded-full text-xs font-bold text-white ${VENTURE_COLORS[vName]}`}>{vName}</span>
                          </label>
                        ))}
                      </div>
                      <textarea name='notes' defaultValue={customerModal.notes||''} className="w-full my-2 border rounded p-1 text-sm" rows={3} placeholder="Add internal notes/review..." />
                      <button type='submit' className="bg-primary text-primary-foreground px-4 py-2 rounded-lg font-semibold w-full mt-1">Save/Close</button>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          )}
          {section === "suppliers" && (
            <div className="bg-muted rounded-xl p-6 shadow-inner">
              <div className="mb-3 flex flex-col sm:flex-row sm:items-center gap-3 w-full justify-between">
                <div className="flex items-center gap-2 w-full max-w-sm">
                  <span className="text-muted-foreground"><Search size={18}/></span>
                  <input
                    type="text"
                    className="border bg-background rounded px-3 py-2 w-full text-sm"
                    placeholder="Search suppliers..."
                    value={supplierSearch}
                    onChange={e=>setSupplierSearch(e.target.value)}
                    aria-label="Search suppliers"
                  />
                  <select
                    className="ml-1 border px-2 py-1 rounded text-sm text-muted-foreground"
                    value={supplierStarFilter}
                    onChange={e=>setSupplierStarFilter(Number(e.target.value))}
                    aria-label="Filter by rating"
                  >
                    <option value={0}>All ★</option>
                    <option value={5}>★5</option>
                    <option value={4}>★4 & up</option>
                    <option value={3}>★3 & up</option>
                    <option value={2}>★2 & up</option>
                    <option value={1}>★1 & up</option>
                  </select>
                  <select
                    className="ml-2 border px-2 py-1 rounded text-sm text-muted-foreground"
                    value={supplierFilter}
                    onChange={e=>setSupplierFilter(e.target.value as any)}
                    aria-label="Sort"
                  >
                    <option value="recent">Most Recent</option>
                    <option value="az">A–Z</option>
                    <option value="za">Z–A</option>
                  </select>
                </div>
              </div>
              <div className="max-h-[325px] overflow-y-auto rounded-xl border bg-background divide-y divide-muted-foreground/6 mt-2">
                {[...suppliers]
                    .filter(s => !supplierSearch ||
                      (s.name?.toLowerCase().includes(supplierSearch.toLowerCase()) ||
                       s.email?.toLowerCase().includes(supplierSearch.toLowerCase()) ||
                       s.phone?.toLowerCase().includes(supplierSearch.toLowerCase()))
                    )
                    .filter(s => supplierStarFilter === 0 || (s.rating||0) >= supplierStarFilter)
                    .sort((a,b)=>{
                      if (supplierFilter==='recent') return 0;
                      if (supplierFilter==='az') return (a.name||'').localeCompare(b.name||'');
                      if (supplierFilter==='za') return (b.name||'').localeCompare(a.name||'');
                      return 0;
                    })
                    .map((s,i) => (
                      <div key={s.name+i}
                           className="px-4 py-3 flex items-center gap-3 hover:bg-muted/70 transition cursor-pointer group">
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-primary truncate">{s.name}</div>
                          <div className="text-xs text-muted-foreground truncate">{s.email||s.phone||''}</div>
                        </div>
                        {typeof s.rating==='number'&&s.rating>0 && <span className="text-xs text-yellow-600 font-bold ml-2">★{s.rating}</span>}
                      </div>
                    ))}
              </div>
            </div>
          )}
        </div>
        {section === "financials" && (
          <div className="relative">
            {/* Time range selector top right of cards grid */}
            <div className="absolute right-2 top-0 z-10">
              <TimeRangeSelector value={range} onChange={setRange} />
            </div>
            <div className="grid sm:grid-cols-2 gap-6 mt-10">
              {/* Checking Account Card Example */}
              <div className="bg-muted rounded-xl p-5 shadow-inner flex flex-col gap-2">
                <div className="flex items-center gap-3 mb-1">
                  <span className="bg-background rounded-full p-2"><Building2 size={22} className="text-primary" /></span>
                  <div className="flex-1">
                    <span className="text-lg font-semibold">Main Checking</span>
                    <div className="text-xs text-muted-foreground">1234 Bank</div>
                  </div>
                  <span className="font-bold text-xl text-primary">$6,895</span>
                </div>
                {/* MiniLineChart removed as per instructions */}
              </div>
              {/* Emergency Fund Card Example */}
              <div className="bg-muted rounded-xl p-5 shadow-inner flex flex-col gap-2">
                <div className="flex items-center gap-3 mb-1">
                  <span className="bg-background rounded-full p-2"><PiggyBank size={21} className="text-pink-600" /></span>
                  <div className="flex-1">
                    <span className="text-lg font-semibold">Emergency Fund</span>
                    <div className="text-xs text-muted-foreground">Goal: $15,000</div>
                  </div>
                  <span className="font-bold text-xl text-green-700">$3,200</span>
                </div>
                {/* MiniLineChart removed as per instructions */}
              </div>
              {/* Add Checking/Savings CTA */}
              <button className="bg-primary/90 hover:bg-primary text-primary-foreground font-medium py-3 px-5 mt-2 rounded-xl flex items-center gap-2 shadow w-full sm:col-span-2 text-base transition"><Plus size={20} /> Add Bank Account</button>
            </div>
          </div>
        )}
        {/* New tab for presets */}
        {section === "presets" && (
          <div className="bg-muted rounded-xl p-6 shadow-inner flex flex-col gap-3 items-start min-h-[150px] max-h-[360px] w-full overflow-y-auto">
            <AddProductDialog open={addProductOpen} onOpenChange={setAddProductOpen} onAdd={prod => {
              setProducts(cur => [...cur, { ...prod, id: Date.now() }]);
            }} />
            <div className="flex items-center gap-3 mb-2 w-full">
              <h2 className="font-semibold text-lg">
                <span className={productType === "products" ? "text-primary" : "text-muted-foreground"} style={{cursor:'pointer'}} onClick={()=>setProductType("products")}>Products</span>
                <span className="mx-2 text-muted-foreground">/</span>
                <span className={productType === "services" ? "text-primary" : "text-muted-foreground"} style={{cursor:'pointer'}} onClick={()=>setProductType("services")}>Services</span>
              </h2>
            </div>
            <p className="text-muted-foreground text-base mb-2">Setup, edit, and manage your {productType === "products" ? "product" : "service"} categories here. These presets will appear as options in transaction entry and are fully customizable by industry.</p>
            <button
              className="bg-primary text-primary-foreground rounded-lg px-4 py-2 font-medium flex items-center gap-2 hover:bg-primary/90 transition"
              onClick={() => setAddProductOpen(true)}
            ><Plus size={18}/> Add {productType === "products" ? "Product" : "Service"}</button>
            <ul className="mt-4 w-full">
              {products.length === 0 && <li className="text-muted-foreground italic">No products/services found.</li>}
              {products.map(p => (
                <li key={p.id} className="flex justify-between items-center gap-3 border-b last:border-0 py-2 w-full">
                  {p.image && <img alt='' src={p.image} className="h-10 w-10 rounded object-cover mr-2" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex gap-2 items-center mb-1"><span className="font-bold">{p.name}</span>{p.category && <span className="text-xs text-muted-foreground">{p.category}</span>}</div>
                    {p.description && <div className="text-xs text-muted-foreground truncate">{p.description}</div>}
                  </div>
                  <span className="text-primary font-bold min-w-[70px] text-right">${p.price}</span>
                  <button onClick={()=>setProducts(cur=>cur.filter(x=>x.id!==p.id))} className="p-1 px-2 rounded text-xs text-destructive font-bold hover:bg-destructive/10 ml-2">Remove</button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default VentureDetail;